/* ============================================================================
 * Raisy /pitchdeck — the 16 slides (React + framer-motion).
 * PORTED FROM THE CO-FOUNDER'S DECK: "Raisy — The AI workforce that runs
 * companies", 18-page PDF, 2026-09-13. Slide order + copy follow that PDF
 * page-for-page (pages 2–3 and 10–11 merge into one slide each).
 * AUDIT: playano-video-production/raise/supercharger/02_20260817_supercharger_pitch/RAISY_DECK_AUDIT_20260913.md
 * DESIGN & ANIMATION FIRST: cinematic direction-aware transitions, slow
 * springy entrances, hero count-ups, TAM/SAM/SOM bubbles, the self-learning
 * loop, the shipped-outreach pipeline, and the interest-capture ask.
 * ========================================================================== */
import { motion, Variants } from 'framer-motion';
import { Fragment, ReactElement, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { cn } from '@/lib/utils';
import { MarketByYearTable } from './marketTable';
import {
  MARKET_CAGR,
  MARKET_CUSTOMERS,
  MARKET_SAM,
  MARKET_SAM_BASIS,
  MARKET_SAM_GROWTH,
  MARKET_SOM,
  MARKET_SOM_GROWTH,
  MARKET_TAM,
  MARKET_TAM_GROWTH,
  MARKET_YEARS,
} from './marketByYear';
import {
  EXIT_HORIZON_YEARS,
  M12_ARR,
  M12_CUSTOMERS,
  MIN_TICKET_SIZE,
  POST_MONEY_VALUATION,
  PRE_MONEY_VALUATION,
  PROCEEDS,
  PROCEEDS_ROUND,
  ROUND_EQUITY_PCT,
  ROUND_MOIC,
  ROUND_RUNWAY_MONTHS,
  TICKET_EQUITY_PCT,
  TICKET_SIZE,
} from './deckFinancials';

/* ------------------------------------------------------------------ tokens */
const EASE = [0.22, 1, 0.36, 1] as const; // expo-out — cinematic
const DUR = 0.6; // slide ENTER (between slides) — snappy, don't waste the user's time
const EXIT = 0.35; // slide EXIT — snappy
const SLOW = 1.35; // in-slide content animation — slow, step-by-step

/** Direction-aware slide transition with depth + blur (fast between slides). */
const slideVariants: Variants = {
  enter: (dir: number) => ({
    x: dir * 80,
    opacity: 0,
    scale: 0.985,
    filter: 'blur(6px)',
    transition: { duration: DUR, ease: EASE },
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: DUR, ease: EASE },
  },
  exit: (dir: number) => ({
    x: dir * -80,
    opacity: 0,
    scale: 0.985,
    filter: 'blur(6px)',
    transition: { duration: EXIT, ease: EASE },
  }),
};

/** Staggered fade-up used for blocks of content inside a slide (slow, step-by-step). */
export const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.24, delayChildren: 0.3 } },
};
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 42, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: SLOW, ease: EASE },
  },
};
export const pop: Variants = {
  hidden: { opacity: 0, scale: 0.6, y: 20 },
  show: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 160, damping: 17 } },
};

/* ------------------------------------------------------------------ misc   */
const API = 'https://api.raisy.team/api/v1/waitlist/join';

function logWaitlist(payload: Record<string, string>) {
  void fetch(API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, tab: 'PitchDeck' }),
  }).catch(() => undefined);
}

/** Small mono kicker label. */
export function Kicker({ children }: { children: ReactNode }) {
  return (
    <motion.p className="pd-kick" variants={fadeUp} initial="hidden" animate="show">
      {children}
    </motion.p>
  );
}

/** Hero number that counts up slowly (≈3s) with an easing curve. */
export function CountUp({
  to,
  prefix = '',
  suffix = '',
  duration = 3,
  delay = 0.6,
  decimals = 0,
  fmt,
}: {
  to: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  delay?: number;
  decimals?: number;
  fmt?: (v: number) => string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), delay * 1000);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (!started || !ref.current) return;
    let raf = 0;
    let start = 0;
    const step = (ts: number) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const raw = to * eased;
      const text = fmt
        ? fmt(raw)
        : decimals > 0
          ? raw.toFixed(decimals)
          : String(Math.round(raw));
      if (ref.current) ref.current.textContent = `${prefix}${text}${suffix}`;
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, to, prefix, suffix, duration, decimals, fmt]);

  const initial = `${prefix}${decimals > 0 ? (0).toFixed(decimals) : 0}${suffix}`;
  return (
    <span className="pd-n" ref={ref}>
      {initial}
    </span>
  );
}

/** One stat block inside a `.pd-stats` grid (staggered fade-up). */
export function Stat({
  n,
  l,
  s,
  bare,
}: {
  n?: ReactNode;
  l: ReactNode;
  s?: string;
  bare?: boolean;
}) {
  return (
    <motion.div className="pd-stat" variants={fadeUp} style={{ cursor: 'default' }}>
      {!bare && <div className="pd-n" style={{ color: 'var(--pd-teal-l)' }}>{n}</div>}
      <div className="pd-l">{l}</div>
      {s && <div className="pd-src">{s}</div>}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ shell */
interface ShellProps {
  children: ReactNode;
  className?: string;
  custom: number;
  dir?: number; // kept for clarity; custom carries the direction
  side?: { src: string; alt: string; cap: string };
  bg?: ReactNode; // full-bleed background rendered behind the body (e.g. title image + scrim)
}

/**
 * Full-viewport slide wrapper: direction-aware entrance/exit + scrollable
 * detection so the wheel handler can let tall slides scroll natively.
 */
export function Shell({ children, className, custom, side, bg }: ShellProps) {
  const ref = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [scrollable, setScrollable] = useState(false);

  /*
   * AUTO-FIT — a slide must never need scrolling, on any 16:9 viewport.
   * Measured at the slide's real height (not a nominal 1080): if the body is
   * taller (or wider) than the slide, scale it down about its centre until it
   * fits. ResizeObserver keeps this true on window resizes, and it re-runs once
   * fonts/images have settled. Hidden slides (the print deck, clientHeight 0)
   * are skipped so the PDF export stays 1:1.
   */
  useEffect(() => {
    const el = ref.current;
    const body = bodyRef.current;
    if (!el || !body) return;

    const fit = () => {
      if (el.clientHeight < 100) {
        body.style.transform = '';
        return;
      }
      body.style.transform = ''; // measure natural size first
      const availH = el.clientHeight - 12;
      const availW = el.clientWidth - 8;
      const needH = body.scrollHeight;
      const needW = body.scrollWidth;
      const k = Math.min(1, availH / Math.max(needH, 1), availW / Math.max(needW, 1));
      const scale = Math.max(k, 0.55); // below this the copy is unreadable — shorten the slide instead
      body.style.transform = scale < 0.995 ? `scale(${scale})` : '';
      // Last resort only: if even the floor cannot fit it, allow native scroll.
      setScrollable(needH * scale > availH + 2);
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    ro.observe(body);
    window.addEventListener('resize', fit);
    const settle = setTimeout(fit, 350); // after fonts + lazy images land
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
      clearTimeout(settle);
    };
  }, []);

  return (
    <motion.section
      ref={ref}
      className={cn('pd-slide', scrollable && 'scrollable', side && 'has-side', className)}
      variants={slideVariants}
      custom={custom}
      initial="enter"
      animate="center"
      exit="exit"
    >
      {bg}
      <div className="pd-body" ref={bodyRef}>{children}</div>
      {side && (
        <div className="pd-side">
          <img src={side.src} alt={side.alt} />
          <span className="cap">{side.cap}</span>
        </div>
      )}
    </motion.section>
  );
}

/* ========================================================================
 * PDF p1 · TITLE
 * ====================================================================== */
export function S0Title({ dir }: { dir: number }) {
  return (
    <Shell
      className="pd-title"
      custom={dir}
      bg={
        <>
          <img className="pd-img" src="/pitch/artsy/c.jpg" alt="Raisy" />
          <div className="pd-scrim" />
        </>
      }
    >
      <motion.div className="pd-title-inner" variants={stagger} initial="hidden" animate="show">
        <Kicker>Initializing Raisy…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Raisy
          <small>The AI workforce that runs companies</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          AI prepares. Humans decide. Trust is earned.
        </motion.p>
        <motion.div className="pd-chips" variants={stagger}>
          <motion.span className="pd-chip" variants={pop}>raisy.team</motion.span>
          <motion.span className="pd-chip muted" variants={pop}>Hong Kong</motion.span>
          <motion.span className="pd-chip" variants={pop}>v2026.09</motion.span>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p2–3 · THE HOOK — "What's different about these 5 founders?" then the
 * punchline, then the quantified problem.
 * ====================================================================== */
export function S1Hook({ dir }: { dir: number }) {
  return (
    <Shell
      className="pd-title"
      custom={dir}
      bg={
        <>
          <img className="pd-img" src="/pitch/deck/hook_founders_grey.jpg" alt="A group of founders at a startup summit" />
          <div className="pd-scrim" />
        </>
      }
    >
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The uncomfortable question…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          What's different about these 5 founders?
        </motion.h1>
      </motion.div>
    </Shell>
  );
}

/* PDF p3 · hook beat 2 — the same photo, the reveal, and the quantified failure. */
export function S1HookReveal({ dir }: { dir: number }) {
  return (
    <Shell
      className="pd-title"
      custom={dir}
      bg={
        <>
          <img className="pd-img" src="/pitch/deck/hook_founders_colour.jpg" alt="The same founders, revealed" />
          <div className="pd-scrim" />
        </>
      }
    >
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The answer…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The other 5 founders are dead.
          <small>Half the companies that start do not survive the runway.</small>
        </motion.h1>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: '1fr', maxWidth: 620 }} variants={stagger}>
          <Stat
            n={<CountUp to={50} suffix="%" duration={3} />}
            l={<>startup failure rate — <b>fail within 5 years</b></>}
            s="Source: US Bureau of Labor Statistics, 2025"
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p4 · THE ERA OF THE ONE-PERSON COMPANY
 * ====================================================================== */
export function S2Era({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_network.jpg', alt: 'The network of one-person companies', cap: 'the network' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Analyzing the market shift…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The era of the one-person company is here.
          <small>The founders who adopt AI properly will out-compete everyone else.</small>
        </motion.h1>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 26 }} variants={stagger}>
          <Stat
            n={<CountUp to={36} suffix="%" duration={3.2} />}
            l="Solo-founded of new startups"
            s="Carta, Founder Ownership Report 2026"
          />
          <Stat
            n={<CountUp to={89} suffix="%" duration={3.2} />}
            l="say AI is essential — critical to survival"
            s="Alibaba.com CoCreate Pitch 2026"
          />
          <Stat
            n={<CountUp to={80} suffix="%" duration={3.2} />}
            l={<>Billion-dollar odds — 70–80% odds of the first $1B solo company in 2026</>}
            s="Dario Amodei, Anthropic (Inc., 2025)"
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p5 · POSITIONING — Raisy is the Company OS for founders.
 * ====================================================================== */
export function S3CompanyOS({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_vision.jpg', alt: 'Raisy as the company operating system', cap: 'the system' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Declaring the category…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Raisy is the Company OS for founders.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          <b>Fundraising is the wedge.</b> Every founder needs to raise. Once we're in,
          Raisy becomes the operating system for the entire company.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p6 · WHAT WE SHIP — the shipped outreach loop + the audit trail.
 * ====================================================================== */
const PIPE = [
  { st: '01', b: 'Investor research', i: 'grounded', tip: 'Every investor researched and scored from the web.' },
  { st: '02', b: 'Personalized outreach', i: 'kernel memory', tip: 'Written from your kernel — not a template.' },
  { st: '03', b: 'Human approval', i: 'your gate', tip: 'Nothing leaves without you.' },
  { st: '04', b: 'Send', i: 'tracked', tip: 'Sent, tracked, followed up.' },
];

export function S4Ship({ dir }: { dir: number }) {
  const [tipped, setTipped] = useState<number | null>(null);
  return (
    <Shell custom={dir} className="pd-s10" side={{ src: '/pitch/s_abstract_pipeline.jpg', alt: 'The shipped Raisy pipeline', cap: 'the pipeline' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Running the pipeline…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          This is what we ship.
        </motion.h1>
        <motion.div className="pd-pipe" variants={fadeUp}>
          {PIPE.map((p, i) => (
            <Fragment key={p.st}>
              <motion.div
                className={cn('pd-pnode', tipped === i && 'tipped')}
                initial={{ opacity: 0.35, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.7 + i * 0.42 }}
                onClick={() => setTipped((t) => (t === i ? null : i))}
              >
                <span className="st">{p.st}</span>
                <b>{p.b}</b>
                <i>{p.i}</i>
                <span className="tip">{p.tip}</span>
              </motion.div>
              {i < PIPE.length - 1 && (
                <motion.span
                  className="pd-parr"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={{ delay: 1.1 + i * 0.42 }}
                >
                  →
                </motion.span>
              )}
            </Fragment>
          ))}
        </motion.div>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: '1fr', marginTop: 30, maxWidth: 780 }} variants={stagger}>
          <Stat
            n={<span style={{ fontSize: 'clamp(15px,1.7vw,20px)' }}>AUDIT TRAIL</span>}
            l={<>Every step logged, every decision receipted.</>}
            s="Current build, Sept 6, 2026 · v1.0"
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p7 · PASTE YOUR LINK — the onboarding promise.
 * ====================================================================== */
export function S5PasteLink({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_product.jpg', alt: 'Raisy reading your site', cap: 'the product' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Onboarding…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Paste your link. We do the rest.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          Your website becomes your company. <b>3,552 investors scored.</b> Emails built for a raise.
        </motion.p>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 30 }} variants={stagger}>
          <Stat
            n={<span style={{ fontSize: 'clamp(16px,1.8vw,22px)' }}>One link. No forms. No calls.</span>}
            l="We read your site and build your profile."
          />
          <Stat
            n={<span style={{ fontSize: 'clamp(16px,1.8vw,22px)' }}>Nothing leaves without you.</span>}
            l="You approve. It sends. It tracks."
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p8 · THE TRUST LAYER — the self-learning loop, NVIDIA-style directed
 * cycle. A circular arrow-ring connects the stages; the shared kernel is the hub.
 * ====================================================================== */
const LOOP_STEPS = [
  { n: '01', t: 'Correction', d: 'human rejects an output', a: -45 },
  { n: '02', t: 'Kernel patch', d: 'shared versioned kernel', a: 45 },
  { n: '03', t: '12 bots improve', d: 'every user of the kernel', a: 135 },
  { n: '04', t: 'Better outcomes', d: 'fewer errors · more trust', a: 225 },
];
const LOOP_R = 46; // % from center (stages sit OUTSIDE the arrow ring)
const LOOP_ARCS = [
  'M 66.97 33.03 A 24 24 0 0 1 66.97 66.97',
  'M 66.97 66.97 A 24 24 0 0 1 33.03 66.97',
  'M 33.03 66.97 A 24 24 0 0 1 33.03 33.03',
  'M 33.03 33.03 A 24 24 0 0 1 66.97 33.03',
];

function SelfLearningLoop() {
  return (
    <div className="pd-loop">
      <svg className="pd-loop-ring" viewBox="0 0 100 100" aria-hidden>
        <defs>
          <linearGradient id="pdRingGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0AB3BF" />
            <stop offset="100%" stopColor="#4DD4E0" />
          </linearGradient>
          <marker id="pdArr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4.5" markerHeight="4.5" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#4DD4E0" />
          </marker>
        </defs>
        <circle cx="50" cy="50" r="24" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.6" />
        {LOOP_ARCS.map((d, i) => (
          <motion.path
            key={i}
            d={d}
            fill="none"
            stroke="url(#pdRingGrad)"
            strokeWidth="1.6"
            strokeLinecap="round"
            markerEnd="url(#pdArr)"
            initial={{ opacity: 0, pathLength: 0 }}
            animate={{ opacity: 1, pathLength: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 1 + i * 0.5 }}
          />
        ))}
      </svg>

      <div className="pd-loop-orbit" aria-hidden>
        <span className="pd-loop-ball" />
      </div>

      <div className="pd-loop-center">
        <span className="lc-brand">Raisy</span>
        <span className="lc-sub">
          shared kernel
          <br />
          self-learning
        </span>
      </div>

      {LOOP_STEPS.map((nd, i) => {
        const rad = (nd.a * Math.PI) / 180;
        return (
          <motion.div
            key={nd.n}
            className="pd-loop-cnode"
            style={{
              left: `calc(50% + ${(LOOP_R * Math.cos(rad)).toFixed(2)}%)`,
              top: `calc(50% + ${(LOOP_R * Math.sin(rad)).toFixed(2)}%)`,
              x: '-50%',
              y: '-50%',
            }}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 140, damping: 15, delay: 0.5 + i * 0.5 }}
          >
            <span className="ln">{nd.n}</span>
            <b>{nd.t}</b>
            <i>{nd.d}</i>
          </motion.div>
        );
      })}

      <motion.div
        className="pd-loop-badge"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 + (LOOP_STEPS.length + 1) * 0.5 }}
      >
        self-learning loop
      </motion.div>
    </div>
  );
}

export function S6TrustLayer({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_moat.jpg', alt: 'Raisy at the shared kernel network', cap: 'the network' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Training cross-company memory…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The trust layer that grows with you.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          One correction improves every bot. <b>Your data trains your kernel, not a competitor's.</b>
        </motion.p>
        <motion.div variants={fadeUp}>
          <SelfLearningLoop />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p9 · BUILT TO THE HIGHEST STANDARDS
 * ====================================================================== */
export function S7Standards({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Running governance checks…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Built to the highest standards.
          <small>Everything is prepared before it's sent — by default.</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          We align with ISO 9001 quality management and GDPR/UWG §7 compliance.
          Your data. Your model. Not ours. An audit trail on every decision.
        </motion.p>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 30 }} variants={stagger}>
          <Stat
            n={<span style={{ fontSize: 'clamp(15px,1.6vw,20px)' }}>Security</span>}
            l="Your data. Your model. Not ours."
          />
          <Stat
            n={<span style={{ fontSize: 'clamp(15px,1.6vw,20px)' }}>ISO 9001</span>}
            l="Quality management alignment"
          />
          <Stat
            n={<span style={{ fontSize: 'clamp(15px,1.6vw,20px)' }}>GDPR / UWG §7</span>}
            l="An audit trail on every decision"
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p10–11 · THEY SELL A TOOL. WE BUILD THE OPERATING SYSTEM.
 * ====================================================================== */
const COMPARE: Array<[string, string, string, string]> = [
  ['What it is', 'Fundraising CRM', 'Fundraising engine', 'Company OS'],
  ['Continues after the round', 'No', 'No', 'Yes — memory persists'],
  ['Expands to sales/recruiting', 'No', 'No', 'Yes'],
  ['Pricing (Annual)', '$2,400+', 'Free (paid tier not public)', '~$1,000 (Growth tier)'],
  ['Governance', 'None', 'Basic', 'Full (ISO, GDPR, audit trail)'],
];

export function S8ToolVsOS({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Mapping the field…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          They sell a tool. We build the operating system.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp} style={{ fontSize: 'clamp(12px,1.3vw,15px)' }}>
          Metal is a fundraising CRM. Platvix is a fundraising engine. Both end at the round close.
          Raisy starts with fundraising, then becomes your sales, recruiting, support, and investor
          relations layer — because the memory persists and the governance scales.
        </motion.p>
        <motion.div className="pd-econ" variants={fadeUp} style={{ marginTop: 26 }}>
          <table className="econ-tbl">
            <thead>
              <tr>
                <th></th>
                <th>Metal</th>
                <th>Platvix</th>
                <th>Raisy</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map(([label, metal, platvix, raisy]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{metal}</td>
                  <td>{platvix}</td>
                  <td className="ok">{raisy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p12 · MARKET — TAM / SAM / SOM, drawn concentric on one baseline.
 *
 * THE NUMBERS COME FROM THE FINANCIAL MODEL (D-066/D-067) — the same source as
 * X+1 and the story deck, so no version states a market size the model does not.
 * Source of truth: the generated `./marketByYear.ts`, whose provenance header names
 * the model file, the assumption inputs and the basis of each series (do NOT restate
 * those paths here — they moved once already). The 30-value 5-year series comes from
 * the model behind the ORBIT dashboard. Regenerate, never hand-edit.
 *
 * WHAT CHANGED: this slide carried the co-founder's hand-entered pair ($52.6B TAM
 * from MarketsandMarkets / $72M SAM / $9M SOM), which matched no model; for one
 * pass it then carried a THIRD basis ($72M / $9M) that was in neither the PDF nor
 * the model. Both are gone.
 * ====================================================================== */

/** USD → the CountUp's display value + suffix. Raw dollars NEVER go to CountUp
 *  (it formats whatever it is given, which once printed "$52600000000.0B"). */
const compact = (usd: number) =>
  usd >= 1e9
    ? { dv: usd / 1e9, suf: 'B', dec: 1 }
    : usd >= 1e6
      ? { dv: usd / 1e6, suf: 'M', dec: 1 }
      : { dv: usd / 1e3, suf: 'K', dec: 0 };

/** the same figure as plain text, for prose — a hard-coded `B` in the note under
 *  the diagram printed "SAM 0.1B" once SAM stopped being in billions. */
const short = (usd: number) => {
  const c = compact(usd);
  return `${c.dv.toFixed(c.dec)}${c.suf}`;
};

const LAST = MARKET_YEARS.length - 1;
const FIRST_YEAR = MARKET_YEARS[0];
const LAST_YEAR = MARKET_YEARS[LAST];
const TAM_NOW = MARKET_TAM[LAST];
const SAM_NOW = MARKET_SAM[LAST];
const SOM_NOW = MARKET_SOM[LAST];

const BUBBLES = [
  {
    key: 'tam',
    v: TAM_NOW,
    ...compact(TAM_NOW),
    pre: '$',
    fs: 'clamp(22px,2.8vw,34px)',
    lbl: `TAM · AI-agent software market, ${FIRST_YEAR}–${LAST_YEAR}`,
    src: `TAM — the AI-agent software market, ${FIRST_YEAR} → ${LAST_YEAR} at the model's endpoint CAGR (${(MARKET_TAM_GROWTH[LAST]! * 100).toFixed(1)}%/yr). Source: Raisy financial model (raisyModel.ts).`,
  },
  {
    key: 'sam',
    v: SAM_NOW,
    ...compact(SAM_NOW),
    pre: '$',
    fs: 'clamp(16px,2vw,23px)',
    lbl: 'SAM · founders raising annually',
    /* WAS "= $72M, held flat across the window" — false since the model began
       accumulating cohorts (`samCohorts = 1 + (year − sam_base_year)`,
       raisyModel.ts:489): SAM is $72M in 2026 → $360M by 2030, +49.5%/yr. The deck
       was asserting the opposite of its own source, in the one string that also
       prints into the PDF. Now derived from the generated mirror. */
    src: `SAM — ${MARKET_SAM_BASIS.companiesPerYear.toLocaleString('en-US')} founders raising annually × ~$${MARKET_SAM_BASIS.spendPerCompany.toLocaleString('en-US')}/yr, one cohort added each year (a cohort keeps raising, so it stays addressable): ${short(MARKET_SAM[0])} in ${MARKET_SAM_BASIS.baseYear} → ${short(SAM_NOW)} by ${LAST_YEAR}, ${(MARKET_CAGR.sam * 100).toFixed(1)}%/yr. Source: Raisy financial model (raisyModel.ts).`,
  },
  {
    key: 'som',
    v: SOM_NOW,
    ...compact(SOM_NOW),
    pre: '$',
    fs: 'clamp(13px,1.7vw,18px)',
    lbl: `SOM · bottom-up, ${MARKET_CUSTOMERS[LAST].toLocaleString()} accounts`,
    src: `SOM — paying customers × ~$${MARKET_SAM_BASIS.spendPerCompany.toLocaleString('en-US')} blended ARPU, bottom-up from the signup/conversion/churn build: ${MARKET_SOM_GROWTH.map((g, i) => (g === null ? `${MARKET_YEARS[i]} n/a` : `${MARKET_YEARS[i]} +${(g * 100).toFixed(0)}%`)).join(' · ')}. Source: Raisy financial model (raisyModel.ts).`,
  },
];

/** THE ONE PLACE THE MARKET GEOMETRY IS DECIDED (D-064/D-067) — deliberately the
 *  same function as X+1, so every version draws the same market.
 *
 *  `glowtest_FE/src/components/viewers/glow_10_viewer_tamsamsom.js` sizes its
 *  circles AREA-TRUE (`r = maxRadius · √(value / tamValue)`). Applied here that
 *  renders SOM at a fraction of a pixel, so the radii sit on a DECLARED LOG scale
 *  — monotonic and order-preserving — and the note under the diagram says so.
 *
 *  The power-compression this slide used before (`v^0.24` with a 40 px floor)
 *  pinned SAM *and* SOM to the same 40 px, so two of the three circles were the
 *  same size. Change this function and the note under the diagram changes with it. */
const R_MAX = 152;
const R_MIN = 54;
function marketRadius(value: number): number {
  const logs = BUBBLES.map((b) => Math.log10(b.v));
  const lo = Math.min(...logs);
  const hi = Math.max(...logs);
  return R_MIN + ((Math.log10(value) - lo) / (hi - lo)) * (R_MAX - R_MIN);
}

export function S9Market({ dir }: { dir: number }) {
  const [active, setActive] = useState<string | null>('tam');
  /* The source rides the CURSOR as a fixed overlay. This slide used to render it in
     its own flow (`.pd-bb-detail`), so the block's height changed with its text and
     `Shell`'s auto-fit rescaled the whole slide on every hover (reported
     2026-09-14). An overlay cannot affect layout; a fixed position cannot scroll.
     (`.pd-bb-detail` was also left without a stylesheet when that block was removed,
     which is why this slide's source line had lost its styling.) */
  const [tip, setTip] = useState({ x: -1, y: -1 });
  const detail = BUBBLES.find((b) => b.key === active);
  const moveTip = (e: ReactMouseEvent) => {
    const w = 360;
    setTip({
      x: Math.max(12, Math.min(e.clientX + 16, window.innerWidth - w - 18)),
      y: Math.min(e.clientY + 18, window.innerHeight - 150),
    });
  };
  return (
    <Shell custom={dir} className="pd-s2">
      <motion.div variants={stagger} initial="hidden" animate="show" className="pd-row">
        <Kicker>Sizing the market…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          A global market, with a focused wedge.
        </motion.h1>

        {/* CONCENTRIC, bottom-anchored — the house geometry (glowtest_FE
            `glow_10_viewer_tamsamsom.js`): every circle shares ONE baseline and is
            centred, so TAM contains SAM contains SOM. Radii come from
            `marketRadius()`; nothing here is a literal. */}
        <motion.div
          className="pd-bb"
          variants={fadeUp}
          onMouseMove={moveTip}
          style={{ position: 'relative', width: R_MAX * 2, height: R_MAX * 2, margin: '14px auto 0' }}
        >
          {BUBBLES.map((b, i) => {
            const r = Math.round(marketRadius(b.v));
            const d = r * 2;
            /* the band a ring owns before the next circle starts — the label lives
               in that band, so the position is DERIVED, never a fixed percentage. */
            const nextR = i < BUBBLES.length - 1 ? marketRadius(BUBBLES[i + 1].v) : 0;
            const band = 2 * (r - nextR);
            return (
              <motion.div
                key={b.key}
                className={cn('pd-bb-c', b.key, active === b.key && 'on')}
                initial={{ opacity: 0, scale: 0.72 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', stiffness: 120, damping: 16, delay: 0.5 + i * 0.45 }}
                onMouseEnter={() => setActive(b.key)}
                onMouseLeave={() => setActive(null)}
                onClick={() => setActive((a) => (a === b.key ? null : b.key))}
                /* CENTRED BY ARITHMETIC, not by a CSS transform: framer writes its own
                   `transform` for the scale animation and silently drops the
                   stylesheet's `translateX(-50%)`, which pushed every circle right by
                   its own radius (measured 2026-09-14). left = R_MAX - r puts the
                   centre of a 2r-wide circle on the container's centreline, always. */
                style={{ width: d, height: d, zIndex: i + 1, left: R_MAX - r, bottom: 0 }}
              >
                <span
                  className="pl"
                  style={{ top: Math.round(band * 0.16), left: '50%', transform: 'translateX(-50%)' }}
                >
                  {b.key.toUpperCase()}
                </span>
                <span
                  className="pv"
                  style={{ fontSize: b.fs, top: Math.round(band * 0.36), left: '50%', transform: 'translateX(-50%)' }}
                >
                  <CountUp to={b.dv} prefix={b.pre} suffix={b.suf} decimals={b.dec} duration={3.2} />
                </span>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div className="pd-bb-labels" variants={fadeUp}>
          {BUBBLES.map((b) => (
            <span key={b.key}>
              <b>{b.key.toUpperCase()}</b> {b.lbl.split('·')[1]}
            </span>
          ))}
        </motion.div>
        {/* PRINT ONLY. On screen the sources live in the cursor tooltip, which a PDF
            cannot capture — so the printed page must carry them, plus all 30 values. */}
        <div className="pd-print-only pd-bb-sources">
          {BUBBLES.map((b) => (
            <p key={b.key}>{b.src}</p>
          ))}
        </div>
        <MarketByYearTable />
        <motion.p className="pd-fine" variants={fadeUp}>
          Pulled from the Raisy financial model — {FIRST_YEAR}–{LAST_YEAR}, {MARKET_YEARS.length} years:
          TAM {short(MARKET_TAM[0])} → {short(TAM_NOW)} · SAM {short(MARKET_SAM[0])} →{' '}
          {short(SAM_NOW)} · SOM {short(MARKET_SOM[0])} → {short(SOM_NOW)}. Circles are concentric on one baseline, radii on a{' '}
          <b>log scale</b> — TAM→SOM spans {(TAM_NOW / SOM_NOW).toFixed(0)}×, so a true-area drawing
          is impossible. Radial order is honest; only the spacing between rings is compressed.
          Hover a circle for its source.
        </motion.p>
        {/* the source follows the CURSOR as a fixed overlay — it is out of the layout
            flow, so hovering can never change the slide's height and the auto-fit can
            never re-scale it (the old in-flow block did both — reported 2026-09-14). */}
        {detail && tip.x > 0 && (
          <div className="pd-bb-tip" style={{ left: tip.x, top: tip.y }}>
            {detail.src}
          </div>
        )}
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p13 · PRICING — five free emails, then three simple plans.
 * ====================================================================== */
const PLANS = [
  { n: 'Starter', p: '$49', u: '/mo', v: '50 emails/mo', t: 'Testing' },
  { n: 'Growth', p: '$149', u: '/mo', v: '200 emails/mo', t: 'Active raise', star: true },
  { n: 'Scale', p: '$399', u: '/mo', v: '600 emails/mo', t: 'High volume' },
];

export function S10Pricing({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Calculating the economics…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Five free emails. Then three simple plans.
          <small>Free 5 → Starter → Growth → Scale. One account, round after round.</small>
        </motion.h1>

        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(2,1fr)', marginTop: 20, maxWidth: 860 }} variants={stagger}>
          <Stat n={<CountUp to={50} prefix="$" suffix="K" duration={3} />} l="MRR target — 1,000 paying × $50/mo by month 12" />
          <Stat n={<CountUp to={9} prefix="$" suffix="M" duration={3} />} l="SOM runway target — 5,000 accounts × ~$150/mo (12–18 mo)" />
        </motion.div>

        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 26 }} variants={stagger}>
          {PLANS.map((pl) => (
            <Stat
              key={pl.n}
              n={
                <span>
                  {pl.star && <span style={{ fontSize: 'clamp(12px,1.2vw,15px)', marginRight: 6 }}>★</span>}
                  {pl.p}
                  <span style={{ fontSize: 'clamp(12px,1.2vw,15px)', opacity: 0.7 }}>{pl.u}</span>
                </span>
              }
              l={
                <>
                  <b>{pl.n}</b> — {pl.v} · {pl.t}
                </>
              }
            />
          ))}
        </motion.div>

        <motion.p className="pd-foot" variants={fadeUp} style={{ marginTop: 22 }}>
          First 5 emails free. No card. No trial clock.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p14 · PROOF — design partner · community · dogfooding + the timeline.
 * ====================================================================== */
const MILESTONES = [
  { num: 'Sept 21', title: 'Launch v1', desc: 'and begin raising on Raisy' },
  { num: 'Oct 15', title: '100+ investors', desc: 'researched · 25 meetings booked' },
  { num: 'Nov 30', title: '10+ founders', desc: 'onboarded · first case study published' },
];

export function S11Proof({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_traction.jpg', alt: 'Raisy with rising metrics', cap: 'the proof' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Pulling the proof…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          We're not asking you to believe us. We're showing you.
        </motion.h1>

        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 20 }} variants={stagger}>
          <Stat
            n={<span style={{ fontSize: 'clamp(14px,1.5vw,18px)' }}>Design Partner</span>}
            l={<>Jonathan Weins, Founder/CEO of <b>PopMeals</b>.</>}
          />
          <Stat
            n={<span style={{ fontSize: 'clamp(14px,1.5vw,18px)' }}>Community</span>}
            l={<>Selected for <b>SuperCharger Ventures</b>.</>}
          />
          <Stat
            n={<span style={{ fontSize: 'clamp(14px,1.5vw,18px)' }}>Dogfooding</span>}
            l={<>Running our own <b>$${(PROCEEDS_ROUND / 1e6).toFixed(1)}M raise on Raisy</b> from September 21st.</>}
          />
        </motion.div>

        <motion.div className="pd-journey" variants={fadeUp}>
          <div className="pd-jline">
            <motion.i
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2.2, ease: EASE, delay: 0.8 }}
              style={{ transformOrigin: 'left' }}
            />
          </div>
          <div className="pd-jrow">
            {MILESTONES.map((m, i) => (
              <motion.div
                key={m.num}
                className="pd-jnode"
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.9 + i * 0.35 }}
              >
                <div className="pd-jdot" />
                <div className="pd-jnum">{m.num}</div>
                <div className="pd-jtitle">{m.title}</div>
                <div className="pd-jdesc">{m.desc}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p15 · TEAM — the two founders and the shared track record.
 * ====================================================================== */
export function S12Team({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} className="pd-s13">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Loading team profiles…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Why we are the right team for this.
          <small>9 shipped products · 1 exit · 4 accelerators. Raisy is the one that runs the company; we are raising on it ourselves.</small>
        </motion.h1>
        <motion.div className="pd-team" variants={stagger}>
          <motion.div className="pd-tcard" variants={fadeUp}>
            <img src="/pitch/david.jpg" alt="David Demmer" />
            <div className="tinfo">
              <h3>David Demmer</h3>
              <div className="role">Co-Founder · CEO &amp; CTO</div>
              <p>
                Left his CEO position at a unicorn to focus on Playano. Went through 1
                incubation program and 3 acceleration programs. Shipped 9 products across
                the Playano suite. Awarded hedge-fund manager, having managed over $4B AUM
                multi-asset under SFC license. Ex-CIO/Co-Founder CryptAM (exited /
                Cyberport hall of fame).
              </p>
            </div>
          </motion.div>

          <motion.div className="pd-tcard" variants={fadeUp}>
            <img src="/pitch/simon.jpg" alt="Simon Stegelmeier" />
            <div className="tinfo">
              <h3>Simon Stegelmeier</h3>
              <div className="role">Co-Founder · CMO &amp; Analytics</div>
              <p>
                10+ years building enterprise-grade data workflows (Salesforce, Tableau,
                Data Cloud) for Vaillant, LEGO, ESA, and Accenture. He built the governance
                layer — kernel, memory, audit trail — that makes Raisy enterprise-ready.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * REGISTRY — order is the deck order (PDF pages 1–15). The Ask (p16),
 * Company OS close (p17) and Impressum (p18) slides are mounted separately
 * by the engine.
 * ====================================================================== */
export const DECK_SLIDES: Array<(p: { dir: number }) => ReactElement> = [
  S0Title,
  S1Hook,
  S1HookReveal,
  S2Era,
  S3CompanyOS,
  S4Ship,
  S5PasteLink,
  S6TrustLayer,
  S7Standards,
  S8ToolVsOS,
  S9Market,
  S10Pricing,
  S11Proof,
  S12Team,
];

/** Images to warm the cache on load (fast first paint). */
export const PRELOAD = [
  '/pitch/artsy/c.jpg',
  '/pitch/s_abstract_network.jpg',
  '/pitch/s_vision.jpg',
  '/pitch/s_abstract_pipeline.jpg',
  '/pitch/s_product.jpg',
  '/pitch/s_moat.jpg',
  '/pitch/s_traction.jpg',
  '/pitch/david.jpg',
  '/pitch/simon.jpg',
  '/pitch/raisy_white.png',
];

/* ========================================================================
 * PDF p16 · THE ASK — the round as modelled ($0.9M pre-seed at D-041 sizing), the
 * and the interest capture.
 * ====================================================================== */
const FOUNDER_LINKS = {
  david: {
    email: 'david@raisy.team',
    calendar: 'https://calendly.com/david-playano',
    dataroom: 'https://drive.google.com/drive/folders/raisy-dataraum',
  },
  simon: {
    email: 'simon@raisy.team',
    calendar: 'https://calendly.com/simon-playano',
    dataroom: 'https://drive.google.com/drive/folders/raisy-dataraum',
  },
};

const PRIMARY_CTA = 'Investing';
const SECONDARY_OPTIONS = ['Try the product', 'Build an AI workforce', 'Partnership', 'Referral', 'Other'];

/**
 * Use of proceeds — the four buckets on PDF p16. Read from the GENERATED
 * ./deckFinancials.ts (model → deck mirror) instead of being hardcoded here, so the slide
 * cannot state a split the financial model does not use (FM-0013 D-024/D-027).
 */

export function S16Ask({ dir, nameRef }: { dir: number; nameRef: RefObject<HTMLInputElement | null> }) {
  const [picked, setPicked] = useState<string | null>(null);
  const [msg, setMsg] = useState('');
  const [showFb, setShowFb] = useState(false);
  const fbText = useRef<HTMLTextAreaElement | null>(null);

  const pick = (opt: string) => {
    setPicked(opt);
    logWaitlist({
      name: nameRef.current?.value.trim() || 'anonymous',
      email: nameRef.current?.value.trim() ? 'logged@gate' : 'anonymous@deck',
      code: `INTERESTED:${opt}`,
    });
    setMsg(opt === PRIMARY_CTA
      ? "Thanks — we'll reach out to schedule a partner call."
      : `Thanks — logged. We'll follow up on ${opt.toLowerCase()}.`);
  };

  const sendFb = () => {
    const t = fbText.current?.value.trim() || '(no reason)';
    logWaitlist({
      name: nameRef.current?.value.trim() || 'anonymous',
      email: 'anonymous@deck',
      code: `NOT_INTERESTED:${t.slice(0, 160)}`,
    });
    setMsg('Thanks for the honesty — logged.');
    setShowFb(false);
  };

  return (
    <Shell custom={dir} className="pd-ask">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The ask…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Join the Raisy round
          <small>$${(PROCEEDS_ROUND / 1e6).toFixed(1)}M pre-seed · SAFE · {ROUND_RUNWAY_MONTHS}-month runway · Min ticket $${(MIN_TICKET_SIZE / 1000).toFixed(0)}K</small>
        </motion.h1>

        <motion.p className="pd-terms" variants={fadeUp}>
          <b>1,000</b> paying customers by month 12 · <b>$1.2M</b> ARR target by month 24
        </motion.p>

        <motion.p className="pd-terms" variants={fadeUp} style={{ marginTop: 2, fontSize: 'clamp(11px,1.1vw,13px)', color: 'var(--pd-dim)' }}>
          <b>Use of proceeds</b> · {PROCEEDS.map((p) => `${Math.round(p.pct * 100)}% ${p.label}`).join(' · ')}
        </motion.p>

        {/* Deal terms from the GENERATED ./deckFinancials.ts. X carried no valuation at
            all until now, so the version that IS the co-founder's PDF was the only one
            where a reader could not size a cheque without asking. Money and stake are
            the same at every cheque size, which is the point of stating both. */}
        <motion.p className="pd-terms" variants={fadeUp} style={{ marginTop: 2, fontSize: 'clamp(11px,1.1vw,13px)', color: 'var(--pd-dim)' }}>
          <b>${(PRE_MONEY_VALUATION / 1e6).toFixed(2)}M</b> pre-money · <b>${(POST_MONEY_VALUATION / 1e6).toFixed(2)}M</b> post
          (the SAFE cap) · the round buys <b>{(ROUND_EQUITY_PCT * 100).toFixed(2)}%</b> · a ${(TICKET_SIZE / 1000).toFixed(0)}K ticket{' '}
          <b>{(TICKET_EQUITY_PCT * 100).toFixed(2)}%</b> · <b>{ROUND_MOIC.toFixed(1)}×</b> if the year-{EXIT_HORIZON_YEARS} plan lands
        </motion.p>

        <motion.div className="pd-ask-ctas" variants={stagger}>
          <motion.button
            className={cn('pd-cta-main', picked === PRIMARY_CTA && 'pick')}
            variants={pop}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => pick(PRIMARY_CTA)}
          >
            {PRIMARY_CTA}
            <span>Lead or join the round</span>
          </motion.button>

          <motion.div className="pd-cta-secondary" variants={stagger}>
            {SECONDARY_OPTIONS.map((opt) => (
              <motion.button
                key={opt}
                className={cn(picked === opt && 'pick')}
                variants={pop}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => pick(opt)}
              >
                {opt}
              </motion.button>
            ))}
          </motion.div>
        </motion.div>

        <motion.button className="pd-no" variants={fadeUp} onClick={() => setShowFb((s) => !s)}>
          Not interested — tell us why
        </motion.button>

        <motion.div className={cn('pd-feedback', showFb && 'show')}>
          <textarea ref={fbText} placeholder="What's missing? What would change your mind?" />
          <button onClick={sendFb}>Send feedback</button>
        </motion.div>
        <motion.div className="pd-done" variants={fadeUp}>{msg}</motion.div>

        <motion.div className="pd-founders" variants={stagger}>
          <motion.div className="pd-fcard" variants={fadeUp}>
            <img src="/pitch/david.jpg" alt="David Demmer" />
            <div className="fi">
              <h4>David Demmer</h4>
              <div className="fr">Co-founder · CEO &amp; CTO</div>
              <a href={`mailto:${FOUNDER_LINKS.david.email}`}>{FOUNDER_LINKS.david.email}</a>
              <div className="flinks">
                <a href={FOUNDER_LINKS.david.calendar} target="_blank" rel="noopener noreferrer">Book a call</a>
                <a href={FOUNDER_LINKS.david.dataroom} target="_blank" rel="noopener noreferrer">Data room</a>
              </div>
            </div>
          </motion.div>
          <motion.div className="pd-fcard" variants={fadeUp}>
            <img src="/pitch/simon.jpg" alt="Simon Stegelmeier" />
            <div className="fi">
              <h4>Simon Stegelmeier</h4>
              <div className="fr">Co-founder · CMO &amp; Analytics</div>
              <a href={`mailto:${FOUNDER_LINKS.simon.email}`}>{FOUNDER_LINKS.simon.email}</a>
              <div className="flinks">
                <a href={FOUNDER_LINKS.simon.calendar} target="_blank" rel="noopener noreferrer">Book a call</a>
                <a href={FOUNDER_LINKS.simon.dataroom} target="_blank" rel="noopener noreferrer">Data room</a>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.p className="pd-foot" variants={fadeUp}>
          raisy.team · Hong Kong
          <span className="brandline">from founders, for founders</span>
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p18 · IMPRESSUM — legal / not-investment-advice footer slide (last).
 * ====================================================================== */
export function S17Impressum({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} className="pd-impressum">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Legal disclaimer</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Not investment advice
          <small>Please read before acting on anything in this deck.</small>
        </motion.h1>

        <motion.div className="pd-impressum-body" variants={stagger}>
          <motion.p variants={fadeUp}>
            <b>No offer or advice.</b> This document is provided by Playano Education Ltd. for
            information and discussion only. It is not an offer to sell, a solicitation to buy, or
            investment, legal, tax, or financial advice, and should not be relied upon as such.
          </motion.p>
          <motion.p variants={fadeUp}>
            <b>Forward-looking statements.</b> Projections, estimates, and forward-looking
            statements reflect current views only and are subject to risks and uncertainties.
            Actual results may differ materially. Playano Education Ltd. undertakes no obligation
            to update them. Past performance is not indicative of future results.
          </motion.p>
          <motion.p variants={fadeUp}>
            <b>No reliance.</b> This information has not been independently verified. Recipients
            should not rely on it and are strongly encouraged to conduct their own due diligence
            and consult licensed financial, legal, and tax advisors before making any investment
            decision.
          </motion.p>
          <motion.p variants={fadeUp}>
            <b>Private company / high risk.</b> Playano Education Ltd. is a private company.
            Nothing here commits it to raise, allocate, or return capital. Early-stage private
            investments are illiquid, speculative, and carry a high risk of total loss. They are
            suitable only for investors who can sustain such a loss.
          </motion.p>
          <motion.p variants={fadeUp}>
            <b>Confidentiality.</b> This document is confidential and intended solely for the
            recipient. It may not be reproduced, distributed, or transmitted without prior written
            consent. If received in error, delete it and notify Playano Education Ltd. immediately.
          </motion.p>
        </motion.div>

        <motion.p className="pd-foot" variants={fadeUp}>
          david@raisy.team · simon@raisy.team · raisy.team · Hong Kong
          <span className="brandline">from founders, for founders</span>
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * PDF p17 · CLOSE — "You operate software. Raisy operates for you."
 * ====================================================================== */
export function S18CompanyOS({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_vision.jpg', alt: 'The company operating system', cap: 'the system' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The long-term vision…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          You operate software. Raisy operates for you.
          <small>Raisy runs the work that steals your time. You do the work only you can do.</small>
        </motion.h1>
        <motion.div className="pd-stats" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 28 }} variants={stagger}>
          <Stat n={<span style={{ fontSize: 'clamp(18px,2vw,26px)' }}>It learns.</span>} l="Every correction patches the shared kernel." />
          <Stat n={<span style={{ fontSize: 'clamp(18px,2vw,26px)' }}>It decides.</span>} l="Bots prepare; the human at the gate decides." />
          <Stat n={<span style={{ fontSize: 'clamp(18px,2vw,26px)' }}>It runs.</span>} l="The work keeps moving without you pushing it." />
        </motion.div>
        <motion.p className="pd-sub" variants={fadeUp} style={{ marginTop: 26 }}>
          <b>Humans build the relationships. Raisy runs the company.</b>
        </motion.p>
        <motion.p className="pd-foot" variants={fadeUp}>
          raisy.team · Hong Kong
          <span className="brandline">v2026.09</span>
        </motion.p>
      </motion.div>
    </Shell>
  );
}
